import { useCallback, useEffect, useState } from 'react'
import { adminListPlans, adminSavePlan, adminSeedPlans } from '../../lib/admin'
import type { AdminPlanRow } from '../../lib/admin'
import type { ExportBranding, FeatureKey, Plan } from '../../lib/plans'
import { AdminPage } from './Admin'

/**
 * The price list, editable.
 *
 * This screen is the reason entitlements are data. Every limit below is read
 * by both apps at runtime from `plans/{id}`, so changing a number here changes
 * what customers can do without a deploy, a release, or an App Review cycle.
 *
 * A plan with no document runs on the built-in default. That is not a broken
 * state — it is the safe one, and it means deleting a document can never lock
 * an account out. "Publish defaults" simply materialises them so there is
 * something to edit.
 */
const FEATURES: Array<{ key: FeatureKey; label: string }> = [
  { key: 'production_docs', label: 'Production documents' },
  { key: 'calendar_export', label: 'Calendar export' },
  { key: 'duplicate_tour', label: 'Duplicate tour' },
  { key: 'venue_autofill', label: 'Venue autofill' },
  { key: 'shared_venue_library', label: 'Shared venue library' },
  { key: 'tour_books', label: 'Tour books' },
  { key: 'roster_view', label: 'Roster view' },
  { key: 'artist_profiles', label: 'Artist profiles' },
  { key: 'per_artist_permissions', label: 'Per-artist permissions' },
  { key: 'sso', label: 'SSO' },
]

const BRANDING: ExportBranding[] = ['none', 'custom', 'whitelabel']

export default function AdminPlans() {
  const [plans, setPlans] = useState<AdminPlanRow[]>([])
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState<AdminPlanRow | null>(null)
  const [seeding, setSeeding] = useState(false)

  const load = useCallback(() => {
    setLoading(true)
    setError(null)
    return adminListPlans({})
      .then((result) => setPlans(result.plans))
      .catch((caught: Error) => setError(caught.message))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const unstored = plans.filter((plan) => !plan.isStored).length

  return (
    <AdminPage
      title="Plans"
      lede="What each plan allows. Read by both apps at runtime — changing a number here changes what customers can do, with no deploy and no App Review."
      actions={
        unstored > 0 ? (
          <button
            className="btn btn-secondary"
            disabled={seeding}
            onClick={async () => {
              setSeeding(true)
              try {
                await adminSeedPlans({})
                await load()
              } catch (caught) {
                setError((caught as Error).message)
              } finally {
                setSeeding(false)
              }
            }}
          >
            {seeding ? 'Publishing…' : `Publish ${unstored} default${unstored === 1 ? '' : 's'}`}
          </button>
        ) : undefined
      }
    >
      {loading && plans.length === 0 && <p className="t-caption">Loading…</p>}
      {error && (
        <p className="panel panel-pad t-caption" style={{ color: 'var(--destructive)' }}>
          {error}
        </p>
      )}

      {unstored > 0 && (
        <p className="panel panel-pad t-caption">
          {unstored} plan{unstored === 1 ? '' : 's'} {unstored === 1 ? 'is' : 'are'} running on
          built-in defaults. The apps behave identically either way — publishing just creates
          documents so the values can be edited here.
        </p>
      )}

      <div className="panel rows">
        {plans.map((plan) => (
          <button
            key={plan.id}
            className="row"
            style={{
              padding: '14px 16px',
              gap: 12,
              width: '100%',
              background: 'transparent',
              border: 'none',
              textAlign: 'left',
              cursor: 'pointer',
            }}
            onClick={() => setEditing(plan)}
          >
            <span className="grow stack" style={{ gap: 2, minWidth: 0 }}>
              <span className="row" style={{ gap: 8 }}>
                <span style={{ fontWeight: 600 }}>{plan.displayName}</span>
                <span className="t-spec-caption faint">{plan.id}</span>
                {!plan.isStored && <span className="chip">Default</span>}
                {!plan.sellableOnNative && <span className="chip">Web only</span>}
              </span>
              <span className="t-caption truncate">{summarise(plan)}</span>
            </span>
            <span className="t-spec-caption faint">Edit</span>
          </button>
        ))}
      </div>

      {editing && (
        <PlanEditor
          plan={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null)
            void load()
          }}
        />
      )}
    </AdminPage>
  )
}

function summarise(plan: Plan): string {
  const parts = [
    plan.maxActiveTours === null ? 'Unlimited tours' : `${plan.maxActiveTours} tour${plan.maxActiveTours === 1 ? '' : 's'}`,
    plan.maxEditorSeats === null ? 'unlimited editors' : `${plan.maxEditorSeats} editor${plan.maxEditorSeats === 1 ? '' : 's'}`,
    plan.maxArtists === null ? 'unlimited artists' : `${plan.maxArtists} artist${plan.maxArtists === 1 ? '' : 's'}`,
    `${plan.features.length} feature${plan.features.length === 1 ? '' : 's'}`,
  ]
  if (plan.exportWatermark) parts.push('watermarked exports')
  return parts.join(' · ')
}

/* ------------------------------------------------------------------ editor */

function PlanEditor({
  plan,
  onClose,
  onSaved,
}: {
  plan: AdminPlanRow
  onClose: () => void
  onSaved: () => void
}) {
  const [draft, setDraft] = useState<Plan>(plan)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const set = <K extends keyof Plan>(key: K, value: Plan[K]) =>
    setDraft((current) => ({ ...current, [key]: value }))

  return (
    <div
      role="dialog"
      aria-modal
      aria-label={`Edit ${plan.displayName}`}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 60,
        display: 'grid',
        placeItems: 'center',
        padding: 20,
        background: 'rgb(0 0 0 / 0.5)',
      }}
      onClick={(event) => event.target === event.currentTarget && onClose()}
    >
      <div
        className="panel stack"
        style={{
          gap: 16,
          padding: 24,
          width: 'min(560px, 100%)',
          maxHeight: '86dvh',
          overflow: 'auto',
        }}
      >
        <div className="stack" style={{ gap: 3 }}>
          <span className="t-eyebrow">{draft.id}</span>
          <h2 className="t-display-s">{draft.displayName}</h2>
        </div>

        <Field label="Display name">
          <input
            className="input"
            value={draft.displayName}
            onChange={(event) => set('displayName', event.target.value)}
          />
        </Field>

        <Field label="Tagline">
          <input
            className="input"
            value={draft.tagline}
            onChange={(event) => set('tagline', event.target.value)}
          />
        </Field>

        <div className="form-row" style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }}>
          <LimitField
            label="Active tours"
            value={draft.maxActiveTours}
            onChange={(value) => set('maxActiveTours', value)}
          />
          <LimitField
            label="Editor seats"
            value={draft.maxEditorSeats}
            onChange={(value) => set('maxEditorSeats', value)}
          />
          <LimitField
            label="Artists"
            value={draft.maxArtists}
            onChange={(value) => set('maxArtists', value)}
          />
        </div>

        {/* Not editable, and shown so it's obvious that isn't an oversight.
            Crew invites are the acquisition loop — capping viewers would
            throttle the one thing that brings new accounts in. */}
        <p className="t-caption">
          Viewer seats are unlimited on every plan, including free, and cannot be capped here.
        </p>

        <Field label="Export branding">
          <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
            {BRANDING.map((option) => (
              <Toggle
                key={option}
                label={option}
                on={draft.exportBranding === option}
                onClick={() => set('exportBranding', option)}
              />
            ))}
          </div>
        </Field>

        <Toggle
          label="Watermark free exports"
          on={draft.exportWatermark}
          onClick={() => set('exportWatermark', !draft.exportWatermark)}
        />

        <Toggle
          label="Offer in the Apple apps"
          on={draft.sellableOnNative}
          onClick={() => set('sellableOnNative', !draft.sellableOnNative)}
        />

        <Field label="Features">
          <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
            {FEATURES.map((feature) => (
              <Toggle
                key={feature.key}
                label={feature.label}
                on={draft.features.includes(feature.key)}
                onClick={() =>
                  set(
                    'features',
                    draft.features.includes(feature.key)
                      ? draft.features.filter((entry) => entry !== feature.key)
                      : [...draft.features, feature.key],
                  )
                }
              />
            ))}
          </div>
        </Field>

        {error && (
          <p className="t-caption" style={{ color: 'var(--destructive)' }}>
            {error}
          </p>
        )}

        <div className="row" style={{ gap: 10, justifyContent: 'flex-end' }}>
          <button className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            disabled={saving}
            onClick={async () => {
              setSaving(true)
              setError(null)
              try {
                await adminSavePlan({ planId: draft.id, plan: draft })
                onSaved()
              } catch (caught) {
                setError((caught as Error).message)
                setSaving(false)
              }
            }}
          >
            {saving ? 'Saving…' : 'Save plan'}
          </button>
        </div>
      </div>
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="stack" style={{ gap: 6 }}>
      <span className="t-eyebrow">{label}</span>
      {children}
    </label>
  )
}

/** A limit that can be a number or unlimited — `null` is meaningful here. */
function LimitField({
  label,
  value,
  onChange,
}: {
  label: string
  value: number | null
  onChange: (value: number | null) => void
}) {
  return (
    <div className="stack" style={{ gap: 6 }}>
      <span className="t-eyebrow">{label}</span>
      <input
        className="input"
        type="number"
        min={0}
        value={value ?? ''}
        onChange={(event) =>
          onChange(event.target.value === '' ? null : Number(event.target.value))
        }
      />
      <span className="t-spec-caption faint">{value === null ? 'Unlimited' : 'Leave empty for unlimited'}</span>
    </div>
  )
}

function Toggle({
  label,
  on,
  onClick,
}: {
  label: string
  on: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      className="chip"
      aria-pressed={on}
      onClick={onClick}
      style={{
        cursor: 'pointer',
        background: on ? 'var(--accent-wash)' : 'transparent',
        border: `var(--hairline-width) solid ${on ? 'var(--accent)' : 'var(--hairline)'}`,
        color: on ? 'var(--accent-text)' : 'var(--text-secondary)',
      }}
    >
      {label}
    </button>
  )
}
